/**
 * UI themes — the chrome (HTML/Tailwind) skin around the Phaser bunker.
 *
 * The bunker pixel-art (Phaser canvas, src/theme.ts PALETTE) is intentionally
 * NOT themed — only the surrounding terminal chrome is.
 *
 * Each theme is applied by setting `data-theme` on <html>; index.css holds the
 * matching CSS-variable block. Tailwind's bunker.{bg,panel,deep,terminal,caution}
 * colors resolve to those variables, so ~140 existing class usages repaint at once.
 *
 * Model: `terminal` = primary ink (body text/borders), `caution` = accent/highlight.
 */

export type ThemeId = 'terminal' | 'clay' | 'carbon';

/** Cycle order for the Topbar button. */
export const THEME_ORDER: ThemeId[] = ['terminal', 'clay', 'carbon'];

export interface ThemeMeta {
  /** Short label shown on the cycle button. */
  label: string;
  /** Primary ink hex (mirrors --c-ink) — used for the xterm terminal + swatch. */
  ink: string;
  /** Accent hex (mirrors --c-accent) — used for the xterm cursor + swatch. */
  accent: string;
  /** Deepest background hex (mirrors --c-deep) — xterm background. */
  bg: string;
}

export const THEME_META: Record<ThemeId, ThemeMeta> = {
  // Terminal green — the original bunker look (glow + scanlines on). Was
  // labeled "Pip-Boy"; renamed pre-launch (Bethesda trademark, see CLAUDE.md).
  terminal: { label: 'Terminal', ink: '#39ff14', accent: '#f0c000', bg: '#0a0a0a' },
  // Anthropic "clay" — warm charcoal + coral accent (Claude CLI look). Flat.
  clay: { label: 'Clay', ink: '#c9c6bb', accent: '#d97757', bg: '#1f1e1d' },
  // Carbon — neutral near-black + magenta accent (activity-panel look). Flat.
  carbon: { label: 'Carbon', ink: '#c8c8c4', accent: '#d2447f', bg: '#141414' },
};

const STORAGE_KEY = 'foundr-x-theme';

export function isThemeId(v: unknown): v is ThemeId {
  return typeof v === 'string' && THEME_ORDER.includes(v as ThemeId);
}

export function loadTheme(): ThemeId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isThemeId(raw) ? raw : 'terminal';
  } catch {
    return 'terminal';
  }
}

export function saveTheme(theme: ThemeId): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* localStorage unavailable — non-fatal, theme just won't persist. */
  }
}

/** Set the `data-theme` attribute so the CSS-variable block takes effect. */
export function applyTheme(theme: ThemeId): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = theme;
  }
}

export function nextTheme(theme: ThemeId): ThemeId {
  const i = THEME_ORDER.indexOf(theme);
  return THEME_ORDER[(i + 1) % THEME_ORDER.length];
}
